import { Question } from '@hotjar/webapp/features/Surveys/CreateEditSurvey/components/Question/Question';
import { Stack, tokens } from '@hotjar/hotsauce';
import type { CSSProperties } from 'react';
import styled from '@emotion/styled';
import type { NotDraggingStyle, DraggingStyle } from 'react-beautiful-dnd';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import type { ReactionStyle } from '../../../../../domain/Surveys/Questions/SurveyQuestion';
import { AddQuestionButtonWithIndex } from '../AddQuestionButton';
import type { QuestionTypes, SurveyQuestion } from '../../../../../domain/Surveys/Questions';

const AddButtonWrapper = styled.div(
  ({ isDragging }: { isDragging: boolean }) => `
  margin-left: calc(${tokens.spacer.x1} * 5);
  align-self: start;
  position: absolute;

  transition: all 0;

  ${
    isDragging &&
    `
    transition: all 0;
    visibility: hidden;
  }`
  }
`,
);

const DropOrAddContainer = styled.div(
  ({ isFirst }: { isFirst: boolean }) => `
  margin-top: ${isFirst ? '0' : tokens.spacer.x4};
  position: relative;
  height: ${tokens.spacer.x5};
`,
);

const DropWrapper = styled.div(
  ({ isOver, isVisible }: { isOver: boolean; isVisible: boolean }) => `
  border: 2px dashed ${tokens.color.neutral_500};
  border-radius: ${tokens.radius.m};
  height: ${tokens.spacer.x5};
  align-items: center;
  justify-content: center;
  display: flex;
  visibility: hidden;
  position: absolute;
  left: 0;
  right: 0;
  color: ${tokens.color.text_light};
  background: ${tokens.color.neutral_100};

  ${isVisible && 'visibility: visible;'}

  ${isOver && `border: 2px dashed ${tokens.color.azure};`}
`,
);

const DropZone = ({ id, isVisible }: { id: string; isVisible: boolean }): JSX.Element => {
  const { t } = useTranslation('feedback');
  return (
    <Droppable droppableId={id} type="question-bank">
      {(provided, snapshot) => (
        <DropWrapper
          {...provided.droppableProps}
          ref={provided.innerRef}
          isVisible={isVisible}
          isOver={snapshot.isDraggingOver}
        >
          {t('wizard.section.questions.drop-zone')}
        </DropWrapper>
      )}
    </Droppable>
  );
};

export interface QuestionListProps {
  isQuestionBankDragging: boolean;
  isValidateEnabled: boolean;
  isRtlLanguage: boolean;
  hasSurveyResponses: boolean;
  questions: SurveyQuestion[];
  onAddNewQuestion: (type: QuestionTypes | 'yes-no', index: number) => void;
  onRemoveQuestion: (question: SurveyQuestion) => void;
  onUpdateQuestion: (fieldToUpdate: string[], questionId: string, isQuestionValid: boolean) => void;
  onPreviewReaction: (config: { questionId: number; style: ReactionStyle }) => void;
  reloadPreview: (step: string) => void;
}

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
): CSSProperties => ({
  userSelect: 'none',
  background: isDragging ? 'lightgreen' : 'grey',
  ...draggableStyle,
});

export const QuestionList = ({
  isQuestionBankDragging,
  isValidateEnabled,
  isRtlLanguage,
  hasSurveyResponses,
  questions,
  onAddNewQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
  onPreviewReaction,
  reloadPreview,
}: QuestionListProps): JSX.Element => {
  const isRemoveQuestionEnabled = questions.length > 1;
  return (
    <Stack direction="column" spacing="x0">
      <Droppable droppableId="question-list" isDropDisabled={isQuestionBankDragging}>
        {(droppableProvided) => (
          <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
            {questions.map((question, index) => (
              <Draggable draggableId={question.uuid} index={index} key={question.uuid}>
                {(draggableProvided, snapshot) => (
                  <div
                    ref={draggableProvided.innerRef}
                    style={getItemStyle(
                      snapshot.isDragging,
                      draggableProvided.draggableProps.style,
                    )}
                    {...draggableProvided.draggableProps}
                    {...draggableProvided.dragHandleProps}
                  >
                    <Stack direction="column" spacing="x4">
                      <DropOrAddContainer isFirst={index == 0}>
                        {!isQuestionBankDragging && (
                          <AddButtonWrapper isDragging={snapshot.isDragging}>
                            <AddQuestionButtonWithIndex onSelect={onAddNewQuestion} index={index} />
                          </AddButtonWrapper>
                        )}

                        <DropZone isVisible={isQuestionBankDragging} id={question.uuid} />
                      </DropOrAddContainer>

                      <Question
                        index={index}
                        question={question}
                        questions={questions}
                        onQuestionUpdate={onUpdateQuestion}
                        onPreviewReaction={onPreviewReaction}
                        canChangeQuestionType={!hasSurveyResponses}
                        removable={isRemoveQuestionEnabled}
                        onRemove={onRemoveQuestion}
                        validate={isValidateEnabled}
                        rtl={isRtlLanguage}
                        isDragging={snapshot.isDragging}
                        reloadPreview={reloadPreview}
                      />
                    </Stack>
                  </div>
                )}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
      <DropOrAddContainer isFirst={false}>
        {!isQuestionBankDragging && (
          <AddButtonWrapper isDragging={false}>
            <AddQuestionButtonWithIndex onSelect={onAddNewQuestion} index={questions.length} />
          </AddButtonWrapper>
        )}
        <DropZone isVisible={isQuestionBankDragging} id="at-end" />
      </DropOrAddContainer>
    </Stack>
  );
};
